import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { handleLogout } from "../api/logoutHelper";
import { searchGames } from "../api/games";
import logo from "../assets/logo192.png";
import heartIcon from "../assets/heart.png";
import user from "../assets/user.png";
import { useAuth } from "../AuthContext";

// Get window dimensions for responsive behavior
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

// SVG Icons
const FavoritesIcon = ({ size = 26 }) => (
  <img
    src={heartIcon}
    alt="Favorites"
    style={{ width: size, height: size }}
    className="brightness-0 invert"
  />
);
const ProfileIcon = ({ size = 26 }) => (
  <img
    src={user}
    alt="Profile"
    style={{ width: size, height: size }}
    className="brightness-0 invert"
  />
);

export default function Header() {
  const { user, setUser, blacklist } = useAuth();
  const navigate = useNavigate();
  const windowSize = useWindowSize();
  const isMobile = windowSize.width <= 768;

  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchSuggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Debounced search for autocomplete
  const debounceTimeout = React.useRef(null);

  // Track scroll to add a subtle solid background/shadow instead of always
  // floating a heavy translucent bar over the content.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const fetchSuggestions = useCallback(
    async (query) => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery || trimmedQuery.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setSearchLoading(true);

      try {
        const response = await searchGames({
          query: trimmedQuery,
          limit: 8,
          searchType: "fuzzy",
          blacklist: blacklist || [],
        });

        let suggestions = response?.games || [];

        if (suggestions.length > 0) {
          const lowerQuery = trimmedQuery.toLowerCase();
          suggestions = suggestions.sort((a, b) => {
            const aName = (a.title || a.name || "").toLowerCase();
            const bName = (b.title || b.name || "").toLowerCase();
            if (aName === lowerQuery && bName !== lowerQuery) return -1;
            if (bName === lowerQuery && aName !== lowerQuery) return 1;
            if (aName.startsWith(lowerQuery) && !bName.startsWith(lowerQuery)) return -1;
            if (bName.startsWith(lowerQuery) && !aName.startsWith(lowerQuery)) return 1;
            if (aName.includes(lowerQuery) && !bName.includes(lowerQuery)) return -1;
            if (bName.includes(lowerQuery) && !aName.includes(lowerQuery)) return 1;
            return 0;
          });

          const finalSuggestions = suggestions.slice(0, 8);
          setSuggestions(finalSuggestions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (error) {
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setSearchLoading(false);
      }
    },
    [blacklist]
  );

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (value.trim().length >= 2) {
      debounceTimeout.current = setTimeout(() => {
        fetchSuggestions(value);
      }, 250);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (searchTerm.trim()) {
      setShowSuggestions(false);
      navigate(`/?search=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm("");
    }
  };

  const handleSuggestionClick = (game) => {
    setShowSuggestions(false);
    setSearchTerm("");
    navigate(`/game/${game.id}`);
  };

  const handleSearchFocus = () => {
    if (searchSuggestions.length > 0 && searchTerm.length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleSearchBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleFavoritesClick = () => {
    if (!user) {
      navigate("/auth/signin", {
        state: { message: "Please log in to view your favorites" },
      });
    } else {
      navigate("/favorites");
    }
  };

  const handleProfileClick = () => {
    if (user) {
      setShowDropdown((d) => !d);
    } else {
      navigate("/auth/signin");
    }
  };

  const handleLogoutClick = () => {
    handleLogout();
    setUser(null);
    setShowDropdown(false);
    navigate("/");
  };

  const navLinkClass =
    "relative text-white/85 font-semibold text-[1rem] px-1 py-1 transition-colors duration-150 hover:text-brand-gold after:absolute after:left-1 after:right-1 after:-bottom-0.5 after:h-[2px] after:rounded-full after:bg-gold-gradient after:origin-left after:scale-x-0 after:transition-transform after:duration-200 hover:after:scale-x-100";

  return (
    <header
      className={[
        "fixed inset-x-0 top-0 z-40 h-[var(--header-height-mobile)] md:h-[var(--header-height)]",
        "flex items-center justify-between px-4 md:px-8",
        "border-b transition-all duration-300",
        scrolled
          ? "bg-surface/90 border-brand-purple/40 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-xl"
          : "bg-surface/60 border-white/5 backdrop-blur-md",
      ].join(" ")}
    >
      {/* thin brand glow line under the header */}
      <span className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-gold/60 to-transparent" />

      {/* Logo + wordmark */}
      <div className="flex items-center min-w-[70px] md:min-w-[110px] h-full shrink-0">
        <Link to="/" className="group flex items-center gap-2.5 h-full">
          <span className="relative flex items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-brand-purple/40 blur-lg opacity-70 group-hover:opacity-100 transition-opacity duration-300" />
            <img
              src={logo}
              alt="Game Goblin Logo"
              className="relative h-[120px] object-contain drop-shadow-[0_2px_10px_rgba(151,1,251,0.5)] transition-transform duration-300 group-hover:scale-105"
            />
          </span>
          {/* <span className="hidden sm:flex flex-col leading-none">
            <span className="font-heading font-extrabold text-lg md:text-xl tracking-wide text-white">
              Game<span className="gg-gradient-text">Goblin</span>
            </span>
            <span className="text-[0.6rem] md:text-[0.65rem] font-semibold uppercase tracking-[0.25em] text-brand-gold/80">
              Game Finder
            </span>
          </span> */}
        </Link>
      </div>

      {/* Search Box - hidden on mobile */}
      {!isMobile && (
        <div className="flex-1 flex justify-center items-center max-w-[460px] mx-5">
          <form onSubmit={handleSearchSubmit} className="w-full relative">
            <div className="relative w-full group">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/50 group-focus-within:text-brand-gold transition-colors">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.2-3.2" />
                </svg>
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchInputChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder={searchLoading ? "Searching..." : "Find a game..."}
                className={[
                  "w-full h-11 rounded-full pl-11 pr-12 text-sm text-white bg-white/[0.06]",
                  "border outline-none transition-all duration-200 placeholder:text-white/45",
                  searchLoading
                    ? "border-brand-gold/70 shadow-glow-gold"
                    : "border-white/10 focus:border-brand-purple-light/70 focus:bg-white/[0.09] focus:shadow-glow-purple",
                ].join(" ")}
              />
              <button
                type="submit"
                aria-label="Search"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-full bg-brand-gradient text-white shadow-[0_2px_10px_rgba(151,1,251,0.4)] hover:-translate-y-0.5 hover:shadow-glow-purple transition-all duration-200"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.2-3.2" />
                </svg>
              </button>

              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-[calc(100%+8px)] left-0 right-0 max-h-[320px] overflow-y-auto rounded-2xl border border-white/10 bg-surface-light/95 shadow-[0_16px_40px_rgba(0,0,0,0.55)] backdrop-blur-xl z-50 animate-fadeIn p-1.5">
                  {searchSuggestions.map((game) => (
                    <div
                      key={game.id}
                      onClick={() => handleSuggestionClick(game)}
                      className="group/item flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl cursor-pointer text-white transition-colors duration-150 hover:bg-brand-purple/20"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="h-6 w-6 shrink-0 flex items-center justify-center rounded-md bg-brand-gold/15 text-brand-gold text-xs">🎮</span>
                        <span className="font-medium text-sm truncate">
                          {game.title || game.name}
                        </span>
                      </div>
                      {game.release_date && (
                        <span className="text-xs text-white/50 shrink-0">
                          {new Date(game.release_date).getFullYear()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Right side */}
      <div className="flex items-center gap-3 md:gap-6 h-full">
        {!isMobile ? (
          <>
            <nav className="flex items-center gap-4 h-full">
              <Link to="/" className={navLinkClass} title="Search and discover games">
                Find games
              </Link>
              <Link to="/games" className={navLinkClass}>
                Browse games
              </Link>
              <Link to="/about" className={navLinkClass}>
                About us
              </Link>
            </nav>

            <button
              onClick={handleFavoritesClick}
              title="Favorites"
              className="flex items-center justify-center h-10 w-10 rounded-full border border-white/10 bg-white/[0.04] transition-all duration-200 hover:border-brand-gold/50 hover:bg-brand-gold/10 hover:-translate-y-0.5"
            >
              <FavoritesIcon size={20} />
            </button>

            {user ? (
              <div className="relative">
                <button
                  onClick={handleProfileClick}
                  title="Profile"
                  className="flex items-center justify-center h-10 w-10 rounded-full border border-brand-purple/40 bg-brand-purple/15 transition-all duration-200 hover:border-brand-purple-light hover:bg-brand-purple/25 hover:-translate-y-0.5"
                >
                  <ProfileIcon size={20} />
                </button>
                {showDropdown && (
                  <div className="absolute right-0 top-[calc(100%+10px)] min-w-[168px] rounded-2xl border border-white/10 bg-surface-light/95 backdrop-blur-xl shadow-[0_16px_40px_rgba(0,0,0,0.55)] overflow-hidden animate-fadeIn z-50 p-1.5">
                    <div
                      className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-white cursor-pointer hover:bg-brand-purple/20 transition-colors"
                      onClick={() => {
                        setShowDropdown(false);
                        navigate("/profile");
                      }}
                    >
                      <span className="text-brand-gold">👤</span> Profile
                    </div>
                    <div
                      className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-white cursor-pointer hover:bg-red-500/15 hover:text-red-300 transition-colors"
                      onClick={handleLogoutClick}
                    >
                      <span>⎋</span> Logout
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2.5 text-white">
                <Link to="/auth/signin" className={navLinkClass}>
                  Login
                </Link>
                <Link
                  to="/auth/signup"
                  className="rounded-full bg-brand-gradient px-5 py-2 text-sm font-heading font-bold text-white shadow-[0_4px_14px_rgba(151,1,251,0.35)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-glow-purple"
                >
                  Sign up
                </Link>
              </div>
            )}
          </>
        ) : (
          /* Mobile Menu */
          <div className="relative flex items-center gap-2">
            <button
              onClick={handleFavoritesClick}
              title="Favorites"
              className="flex items-center justify-center h-9 w-9 rounded-full border border-white/10 bg-white/[0.04] transition-colors duration-150 hover:bg-brand-gold/10"
            >
              <FavoritesIcon size={20} />
            </button>

            <button
              onClick={() => setShowMobileMenu((v) => !v)}
              title="Menu"
              className="flex items-center justify-center h-9 w-9 rounded-full border border-brand-purple/50 bg-brand-purple/15 text-brand-gold font-bold text-lg transition-colors duration-150 hover:bg-brand-purple/25"
            >
              ☰
            </button>

            {showMobileMenu && (
              <div className="absolute right-0 top-[calc(100%+10px)] w-[280px] rounded-2xl border border-white/10 bg-surface-light/95 backdrop-blur-xl shadow-[0_16px_40px_rgba(0,0,0,0.55)] p-4 z-50 animate-fadeIn">
                {/* Mobile search */}
                <div className="mb-4 pb-4 border-b border-brand-gold/20">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="relative w-full">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchInputChange}
                        onFocus={handleSearchFocus}
                        onBlur={handleSearchBlur}
                        placeholder={searchLoading ? "Searching..." : "Find game..."}
                        className="w-full h-9 rounded-full pl-4 pr-11 text-base text-white bg-white/10 border-2 border-brand-gold/40 outline-none focus:border-brand-gold/80 transition-colors placeholder:text-white/60"
                      />
                      <button
                        type="submit"
                        aria-label="Search"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-full text-brand-gold"
                      >
                        🔍
                      </button>
                    </div>
                  </form>
                </div>

                {/* Mobile nav */}
                <div className="flex flex-col gap-1 mb-4 pb-4 border-b border-brand-gold/20">
                  <Link
                    to="/"
                    onClick={() => setShowMobileMenu(false)}
                    className="text-white font-medium px-3 py-2.5 rounded-lg transition-colors hover:bg-brand-gold/10"
                  >
                    Find game
                  </Link>
                  <Link
                    to="/games"
                    onClick={() => setShowMobileMenu(false)}
                    className="text-white font-medium px-3 py-2.5 rounded-lg transition-colors hover:bg-brand-gold/10"
                  >
                    Browse games
                  </Link>
                  <Link
                    to="/about"
                    onClick={() => setShowMobileMenu(false)}
                    className="text-white font-medium px-3 py-2.5 rounded-lg transition-colors hover:bg-brand-gold/10"
                  >
                    About us
                  </Link>
                </div>

                {/* Mobile auth/profile */}
                {user ? (
                  <div className="flex flex-col gap-1">
                    <div
                      onClick={() => {
                        setShowMobileMenu(false);
                        navigate("/profile");
                      }}
                      className="text-white font-medium px-3 py-2.5 rounded-lg cursor-pointer transition-colors hover:bg-brand-gold/10"
                    >
                      Profile
                    </div>
                    <div
                      onClick={() => {
                        setShowMobileMenu(false);
                        handleLogoutClick();
                      }}
                      className="text-white font-medium px-3 py-2.5 rounded-lg cursor-pointer transition-colors hover:bg-brand-gold/10"
                    >
                      Logout
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <Link
                      to="/auth/signup"
                      onClick={() => setShowMobileMenu(false)}
                      className="text-white font-medium px-3 py-2.5 rounded-lg transition-colors hover:bg-brand-gold/10"
                    >
                      Signup
                    </Link>
                    <Link
                      to="/auth/signin"
                      onClick={() => setShowMobileMenu(false)}
                      className="text-white font-medium px-3 py-2.5 rounded-lg transition-colors hover:bg-brand-gold/10"
                    >
                      Login
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
